import React from 'react';
import PropTypes from 'prop-types';
import { App } from '@meronex/app';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import { useTheme } from '@material-ui/core/styles';

import IAudit from '@meronex/icons/fi/FiClipboard';
import IChart from '@meronex/icons/fi/FiBarChart2';
import IHealth from '@meronex/icons/fi/FiShield';
import IBox from '@meronex/icons/fi/FiPackage';
import MdClose from '@meronex/icons/md/MdClose';

import Transactions from '../../components/storeItems/actions/Transactions';

import { storesMng } from '../../api/storesMng';
import Filter from '../../../common/Filter';
import StoreAnalyticsDialog from '../storeAnalytics/StoreAnalyticsDialog';
import HealthCheckDialog from './HealthCheckDialog';

function AuditStoreDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const { open, onClose, store } = props;

  const [filters, setFilters] = React.useState();
  const [openAnalytics, setOpenAnalytics] = React.useState(false);
  const [openHealthCheck, setOpenHealthCheck] = React.useState(false);

  const transactionsRef = React.useRef();

  const [fetchValues, setFetchValues] = React.useState({
    skip: 0,
    limit: 9,
  });

  React.useEffect(() => {
    if (store) {
      fetchStoreTransactions({
        limit: fetchValues.limit,
        skip: fetchValues.skip,
      });
      if (transactionsRef.current) {
        transactionsRef.current.refresh();
      }
    }
  }, [filters]);

  if (!store) {
    return null;
  }

  const fetchStoreTransactions = async ({ limit, skip }) => {
    if (!store) return null;
    const fv = {
      storeId: store._id,
      limit,
      skip,
      filters,
    };
    const transactions = await storesMng.getStoreTransactionsCursor(fv);
    setFetchValues(fv);
    return transactions;
  };

  const onApplyFilters = (filters) => {
    setFilters(filters);
  };

  const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8';
  const accentColor = isDark ? 'rgba(99,102,241,0.8)' : '#6366f1';

  return (
    <Dialog
      className={'fullScreenMobile'}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle style={{ paddingBottom: '0' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            flexShrink: 0,
          }}>
            <IAudit size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#1e293b',
            }}>
              Audit {store.name}
            </div>
            <div style={{
              fontSize: '12px',
              color: mutedColor,
              marginTop: '1px',
            }}>
              <IBox size={11} style={{ position: 'relative', top: '1px', marginRight: '4px' }} />
              {(store.itemCount || 0).toLocaleString()} items in store
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div
              onClick={() => setOpenHealthCheck(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor: isDark ? 'rgba(76,175,80,0.1)' : '#f0fdf4',
                color: isDark ? 'rgba(76,175,80,0.8)' : '#16a34a',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(76,175,80,0.18)' : '#dcfce7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(76,175,80,0.1)' : '#f0fdf4'; }}>
              <IHealth size={14} />
              <span>Health Check</span>
            </div>
            <div
              onClick={() => setOpenAnalytics(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor: isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff',
                color: accentColor,
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.18)' : '#e0e7ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff'; }}>
              <IChart size={14} />
              <span>Analytics</span>
            </div>
          </div>
          <div
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              flexShrink: 0,
              fontSize: '20px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <MdClose />
          </div>
        </div>
      </DialogTitle>
      <DialogContent style={{ padding: '0' }}>
        {/* Filter bar */}
        <div style={{
          padding: '12px 24px',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
        }}>
          <Filter
            expandable
            filterOnApply
            placeholder="Search item name..."
            enableDateRange
            onReset={() => setFilters({})}
            onApply={(v) => {
              const _filters = Object.assign({ ...filters }, v);
              onApplyFilters(_filters);
            }}
          />
        </div>
        {/* Transactions list */}
        <Transactions
          ref={transactionsRef}
          isStoreTransactions
          onReverseTransaction={() => {
            onClose();
          }}
          height={window.innerHeight - 240}
          fetchTransactions={fetchStoreTransactions}
        />
      </DialogContent>
      {store && openAnalytics && (
        <StoreAnalyticsDialog
          storeId={store._id}
          open={openAnalytics}
          onClose={() => setOpenAnalytics(false)}
        />
      )}
      {store && openHealthCheck && (
        <HealthCheckDialog
          open={openHealthCheck}
          onClose={() => setOpenHealthCheck(false)}
          store={store}
        />
      )}
    </Dialog>
  );
}

AuditStoreDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  store: PropTypes.object,
};

export default AuditStoreDialog;
